import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import SearchIcon from "@material-ui/icons/Search";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import useOpenContactConversation from "../../hooks/useOpenContactConversation";
import ContactAvatar from "../ContactAvatar";
import NewTicketModal from "../NewTicketModal";

// A busca só vai ao banco a partir de 3 caracteres e depois de uma pausa na
// digitação — sem isso cada tecla virava uma consulta.
const MIN_SEARCH_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 600;

// Número (ou e-mail) + Clientes vinculados: deixa claro por que o contato
// apareceu numa busca por CNPJ.
const contactSecondaryText = (contact) => {
  const clientes = (Array.isArray(contact?.linkedClientes) ? contact.linkedClientes : [])
    .map((c) => c?.nomeFantasia || c?.nome)
    .filter(Boolean);
  const parts = [contact?.number || contact?.email, clientes.join(", ")].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
};

const useStyles = makeStyles((theme) => ({
  title: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  count: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  list: {
    height: 400,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  empty: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    padding: theme.spacing(4, 2),
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  whatsappButton: {
    color: "#25D366",
  },
}));

// Lista os contatos que têm a tag (ContactTags), com busca e scroll infinito.
// Clicar abre a conversa do contato pelo mesmo fluxo da aba Contatos.
const TagContactsModal = ({ open, onClose, tag }) => {
  const classes = useStyles();
  const history = useHistory();

  const [contacts, setContacts] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchParam, setSearchParam] = useState("");

  const {
    openContact,
    loadingContactId,
    pendingContact,
    closeNewTicketModal,
  } = useOpenContactConversation();

  const tagId = tag?.id;

  // Reabrir o modal (ou trocar de tag) começa do zero.
  useEffect(() => {
    if (!open) return;
    setContacts([]);
    setCount(0);
    setHasMore(false);
    setPageNumber(1);
    setSearchInput("");
    setSearchParam("");
  }, [open, tagId]);

  const typedTerm = searchInput.trim();
  const isTermTooShort =
    typedTerm.length > 0 && typedTerm.length < MIN_SEARCH_CHARS;

  useEffect(() => {
    // Campo limpo volta à lista completa na hora; termo curto também volta à
    // lista completa (sem busca), mas só depois da pausa.
    if (typedTerm === "") {
      setSearchParam("");
      return undefined;
    }
    const timer = setTimeout(() => {
      setSearchParam(typedTerm.length >= MIN_SEARCH_CHARS ? typedTerm : "");
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typedTerm]);

  useEffect(() => {
    setContacts([]);
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    if (!open || !tagId) return;

    const abortController = new AbortController();
    setLoading(true);

    api
      .get("/contacts/", {
        params: {
          tagIds: JSON.stringify([tagId]),
          searchParam,
          // Só cadastro do contato (nome/número/e-mail): sem isso o termo
          // também era procurado no corpo de todas as mensagens (LIKE sem
          // índice em Messages), o que deixava cada busca lenta.
          searchScope: "contact",
          // Também acha pelo CNPJ/CPF do Cliente vinculado ao contato.
          searchClienteDoc: "true",
          pageNumber,
        },
        signal: abortController.signal,
      })
      .then(({ data }) => {
        const page = Array.isArray(data?.contacts) ? data.contacts : [];
        setContacts((prev) => {
          const base = pageNumber === 1 ? [] : prev;
          const seen = new Set(base.map((c) => c.id));
          return [...base, ...page.filter((c) => !seen.has(c.id))];
        });
        setCount(Number(data?.count) || 0);
        setHasMore(!!data?.hasMore);
        setLoading(false);
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setLoading(false);
        toastError(err);
      });

    return () => abortController.abort();
  }, [open, tagId, searchParam, pageNumber]);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber((prev) => prev + 1);
    }
  };

  const handleCloseNewTicket = (ticket) => {
    closeNewTicketModal();
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  return (
    <>
      <NewTicketModal
        modalOpen={!!pendingContact}
        initialContact={pendingContact}
        onClose={handleCloseNewTicket}
      />
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle disableTypography>
          <div className={classes.title}>
            <Typography variant="h6" component="span">
              {i18n.t("tagContactsModal.title")}
            </Typography>
            {tag && (
              <Chip
                variant="outlined"
                size="small"
                label={tag.name}
                style={{
                  backgroundColor: tag.color,
                  textShadow: "1px 1px 1px #000",
                  color: "white",
                }}
              />
            )}
            <span className={classes.count}>
              {i18n.t("tagContactsModal.count", { count })}
            </span>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder={i18n.t("tagContactsModal.search")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            helperText={
              isTermTooShort
                ? i18n.t("tagContactsModal.minChars", { count: MIN_SEARCH_CHARS })
                : undefined
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <List className={classes.list} onScroll={handleScroll}>
            {contacts.map((contact) => (
              <ListItem
                key={contact.id}
                button
                onClick={() => openContact(contact)}
                disabled={loadingContactId === contact.id}
              >
                <ListItemAvatar>
                  {loadingContactId === contact.id ? (
                    <CircularProgress size={40} />
                  ) : (
                    <ContactAvatar contact={contact} />
                  )}
                </ListItemAvatar>
                <ListItemText
                  primary={contact.name}
                  secondary={contactSecondaryText(contact)}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    className={classes.whatsappButton}
                    onClick={() => openContact(contact)}
                    disabled={loadingContactId === contact.id}
                  >
                    <WhatsAppIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {loading && (
              <div className={classes.loading}>
                <CircularProgress size={24} />
              </div>
            )}
            {!loading && contacts.length === 0 && (
              <div className={classes.empty}>
                {i18n.t("tagContactsModal.empty")}
              </div>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            {i18n.t("tagContactsModal.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TagContactsModal;
